// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography, { TitleTypographyProps } from "@plumberssupply/App/Components/TitleTypography/TitleTypography";
import { SwiperStyle } from "@plumberssupply/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import ShopByCategoryItem from "@plumberssupply/App/Widgets/ShopByCategory/ShopByCategoryItem";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum tabFields {
    title = "title",
    products = "products",
}

const enum fields {
    tabs = "tabs",
    title = "title",
}

interface TabsProps {
    fields: {
        title: string;
        products: {
            fields: {
                productId: string;
            };
        }[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isTabletScreen: getXngCommon(state).isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentFeatureStyles {
    shopByGridContainer?: GridContainerProps;
    shopByGridItem?: GridItemProps;
    title?: TitleTypographyProps;
    centeringWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    swiperStyles?: SwiperStyle;
    selectCategoryStylesWrapper?: InjectableCss;
    selectCategoryStyles?: DynamicDropdownPresentationProps;
}

export const shopByStyles: ContentFeatureStyles = {
    shopByGridContainer: {
        gap: 20,
        css: css`
            & > ${GridOffset} {
                width: 100%;
                margin: 0;
                margin-bottom: -36px;
                margin-top: -23px;
                &::after {
                    // Hide the border bottom for widget
                    content: "";
                    width: 100%;
                    border: 4px solid ${color("white")};
                    margin: -2px;
                }
            }
        `,
    },
    title: {
        extendedStyles: {
            wrapper: {
                css: css`
                    margin-bottom: 24px;
                `,
            },
            title: {
                color: "common.ui11",
                weight: 800,
                css: css`
                    margin-bottom: 0;
                    ${xScreen("lte", "xs")} {
                        margin-bottom: 0;
                    }
                `,
            },
        },
    },
    shopByGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            border-bottom: 1px solid ${color("common.ui7")};
            padding: 23px 0;
            ${xScreen("lt", 700)} {
                justify-content: center;
                flex-basis: 100%;
                max-width: 100%;
            }
            ${xScreen(481, 768)} {
                &:nth-child(odd) > div {
                    padding-right: 18px;
                }
                &:nth-child(even) > div {
                    padding-left: 18px;
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
        `,
    },
    tabGroupStyle: {
        mergeCss: true,
        cssOverrides: {
            tabContent: css`
                border-top: 1px solid ${color("common.ui7")};
                box-shadow: none;
                ${xScreen("lte", "sm")} {
                    padding: 16px 0 25px;
                }
            `,
        },
    },
    selectCategoryStylesWrapper: {
        css: css`
            width: 262px;
            padding-bottom: 24px;
            ${xScreen("lt", "xs")} {
                width: 100%;
            }
        `,
    },
};

const styles = shopByStyles;

const ShopByCategory: FC<Props> = ({ id, fields, loadProductInfoList, isTabletScreen }) => {
    const products = useAppSelector(state => getProductsForProductInfoList(state, id));
    const [tabProducts, setTabProducts] = useState<{ title: string; products: string[] }[]>([]);
    const [activeTab, setActiveTab] = useState<{ title?: string; productIds: string[] }>();
    const [productsTabsOptions, setProductsTabsOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        const productsIdsByTab: typeof tabProducts = [];
        const allProductIds: string[] = [];

        fields.tabs.forEach(tab => {
            const { fields } = tab;
            const tabProductsIds = fields.products?.map(product => {
                allProductIds.push(product.fields.productId);
                return product.fields.productId;
            });

            // Hide tab when it doesn't have products in CMS
            if (tabProductsIds.length > 0) {
                productsIdsByTab.push({ title: fields.title, products: tabProductsIds });
                setActiveTab({ title: productsIdsByTab[0].title, productIds: productsIdsByTab[0].products });
            }
        });

        setTabProducts(productsIdsByTab);

        if (allProductIds.length === 0) {
            return;
        }

        loadProductInfoList({
            id,
            getProductCollectionParameter: { productNumbers: allProductIds, pageSize: "9999" } as any,
        });
    }, [fields]);

    useEffect(() => {
        const options = tabProducts.map(tab => ({
            optionText: tab.title,
            optionValue: tab.title,
        }));
        setProductsTabsOptions(options);
    }, [tabProducts]);

    if (tabProducts.length === 0 || !products || products?.length === 0) {
        return null;
    }

    const selectChangeHandler = (selectedCategory?: string) => {
        const correspondingProducts = tabProducts.find(tab => tab.title === selectedCategory)?.products || [];
        setActiveTab({ title: selectedCategory, productIds: correspondingProducts });
    };

    return (
        <StyledWrapper {...styles.centeringWrapper} data-test-selector="ShopByCategory">
            {fields.title && <TitleTypography {...styles.title}>{fields.title}</TitleTypography>}
            {isTabletScreen ? (
                <>
                    <StyledWrapper {...styles.selectCategoryStylesWrapper}>
                        <DynamicDropdown
                            label={translate("Product Category")}
                            onSelectionChange={selectChangeHandler}
                            selected={activeTab?.title}
                            options={productsTabsOptions}
                            data-test-selector="shopByCategorySelect"
                            {...styles.selectCategoryStyles}
                        />
                    </StyledWrapper>
                    <GridContainer {...styles.shopByGridContainer} data-test-selector="shopByCategoryContent">
                        {activeTab?.productIds?.map(productId => {
                            const product = products.find(p => p.productNumber === productId);
                            return (
                                product && (
                                    <GridItem {...styles.shopByGridItem} key={productId}>
                                        <ShopByCategoryItem product={product} widgetId={id} />
                                    </GridItem>
                                )
                            );
                        })}
                    </GridContainer>
                </>
            ) : (
                <TabGroup current={tabProducts[0]?.title} {...styles.tabGroupStyle}>
                    {tabProducts.map(tab => {
                        const productsByTab = products?.filter(product =>
                            tab.products?.includes(product.productNumber),
                        );

                        return (
                            <Tab key={tab.title} headline={tab.title} tabKey={tab.title} {...styles.tabStyle}>
                                <GridContainer {...styles.shopByGridContainer}>
                                    {productsByTab?.map(product => (
                                        <GridItem {...styles.shopByGridItem} key={product.id}>
                                            <ShopByCategoryItem product={product} widgetId={id} />
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            </Tab>
                        );
                    })}
                </TabGroup>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategory),
    definition: {
        displayName: "Shop By Category",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: tabFields.products,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => {
                            return item.fields.productId;
                        },
                        fieldDefinitions: [
                            {
                                name: "productId",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
